/**
 * profileService.ts
 *
 * Client for the `geo-profile` contract.  Tiny surface:
 *
 *   getProfile(owner)        — always returns the full record; owner can
 *                              read their own private fields this way.
 *   getPublicProfile(owner)  — redacted when the owner flipped the
 *                              `is_private` flag; used when rendering
 *                              another user's profile.
 *   setProfile(...)          — owner-authorised write; handles both
 *                              passkey smart accounts (prepare-sign-submit
 *                              flow via `/api/passkey-kit`) and
 *                              Freighter/other wallets (direct
 *                              simulate-sign-submit via the Stellar SDK).
 *   setPrivacy(...)          — same dual-path write, just the flag.
 *
 * All byte caps are enforced here (and on the contract) so the UI can
 * show a live byte counter and reject oversize inputs before the user
 * burns gas on a simulation.
 *
 * Reads go through `/api/contract/readonly` — the existing generic
 * simulation endpoint that the rest of the app already uses.
 */

import {
  Contract,
  rpc as sorobanRpc,
  Networks,
  xdr,
  Address,
  BASE_FEE,
  TransactionBuilder,
} from '@stellar/stellar-sdk';

export const PROFILE_MAX_BYTES = {
  displayName:   32,
  bio:          140,
  avatarUrl:    128,
  xHandle:       32,
  discordHandle: 32,
} as const;

export interface ProfileRecord {
  displayName: string;
  bio: string;
  avatarUrl: string;
  xHandle: string;
  discordHandle: string;
  isPrivate: boolean;
  updatedAtLedger: number;
  version: number;
  /** True when the record has never been written (version === 0). */
  isUnset: boolean;
}

export interface ProfileWriteResult {
  txHash: string;
}

export const EMPTY_PROFILE: ProfileRecord = {
  displayName: '',
  bio: '',
  avatarUrl: '',
  xHandle: '',
  discordHandle: '',
  isPrivate: false,
  updatedAtLedger: 0,
  version: 0,
  isUnset: true,
};

// ── Env resolution ───────────────────────────────────────────────────────────

function profileContractId(): string {
  const id = process.env.REACT_APP_V2_PROFILE_ID;
  if (!id) {
    throw new Error(
      'REACT_APP_V2_PROFILE_ID is not set — deploy the geo-profile contract ' +
      'and add the ID to your environment.'
    );
  }
  return id;
}

function backendBaseUrl(): string {
  if (typeof window === 'undefined') return '';
  const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return isDev ? `${window.location.protocol}//${window.location.hostname}:8080` : '';
}

function appBackendBaseUrl(): string {
  return process.env.REACT_APP_BACKEND_URL || backendBaseUrl() || window.location.origin;
}

// ── Byte validation ──────────────────────────────────────────────────────────

function utf8ByteLength(s: string): number {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(s).length;
  return new Blob([s]).size;
}

export interface ProfileValidationError {
  field: keyof typeof PROFILE_MAX_BYTES;
  maxBytes: number;
  actualBytes: number;
}

export function validateProfile(input: {
  displayName?: string;
  bio?: string;
  avatarUrl?: string;
  xHandle?: string;
  discordHandle?: string;
}): ProfileValidationError | null {
  const fields = ['displayName', 'bio', 'avatarUrl', 'xHandle', 'discordHandle'] as const;
  for (const field of fields) {
    const value = input[field] ?? '';
    const bytes = utf8ByteLength(value);
    if (bytes > PROFILE_MAX_BYTES[field]) {
      return { field, maxBytes: PROFILE_MAX_BYTES[field], actualBytes: bytes };
    }
  }
  return null;
}

export function remainingBytes(
  field: keyof typeof PROFILE_MAX_BYTES,
  value: string,
): number {
  return PROFILE_MAX_BYTES[field] - utf8ByteLength(value);
}

// ── ScVal helpers for decode ─────────────────────────────────────────────────

function scValToNative(scVal: xdr.ScVal): any {
  switch (scVal.type) {
    case 'scvBool':   return scVal.b;
    case 'scvVoid':   return null;
    case 'scvU32':    return scVal.u32;
    case 'scvI32':    return scVal.i32;
    case 'scvU64':    return scVal.u64.toString();
    case 'scvI64':    return scVal.i64.toString();
    case 'scvString': return scVal.str.toString();
    case 'scvSymbol': return scVal.sym.toString();
    case 'scvMap': {
      const out: Record<string, any> = {};
      const entries = scVal.map;
      if (!entries) return out;
      for (const entry of entries) {
        const key = scValToNative(entry.key);
        out[String(key)] = scValToNative(entry.val);
      }
      return out;
    }
    default:
      return scVal.value ?? null;
  }
}

function recordFromScVal(scVal: xdr.ScVal): ProfileRecord {
  const native = scValToNative(scVal);
  if (!native || typeof native !== 'object') return { ...EMPTY_PROFILE };
  const version = Number(native.version || 0);
  return {
    displayName:     String(native.display_name || ''),
    bio:             String(native.bio || ''),
    avatarUrl:       String(native.avatar_url || ''),
    xHandle:         String(native.x_handle || ''),
    discordHandle:   String(native.discord_handle || ''),
    isPrivate:       Boolean(native.is_private),
    updatedAtLedger: Number(native.updated_at_ledger || 0),
    version,
    isUnset:         version === 0,
  };
}

// ── Reads ────────────────────────────────────────────────────────────────────

async function callReadonly(functionName: string, owner: string): Promise<ProfileRecord> {
  const url = `${backendBaseUrl()}/api/contract/readonly`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contractId: profileContractId(),
      functionName,
      parameters: [owner],
    }),
  });
  if (!resp.ok) {
    throw new Error(`geo-profile ${functionName} failed: HTTP ${resp.status}`);
  }
  const data = await resp.json();
  if (!data?.result) return { ...EMPTY_PROFILE };
  const scVal = xdr.ScVal.fromXDR(data.result, 'base64');
  return recordFromScVal(scVal);
}

export function getProfile(owner: string): Promise<ProfileRecord> {
  return callReadonly('get_profile', owner);
}

export function getPublicProfile(owner: string): Promise<ProfileRecord> {
  return callReadonly('get_public_profile', owner);
}

// ── Writes ───────────────────────────────────────────────────────────────────

/**
 * Stellar / Freighter-style wallet surface.  Any object that can sign a
 * base64 XDR string is acceptable — we only ever call `signTransaction`.
 */
export interface SigningWallet {
  getPublicKey(): Promise<string>;
  signTransaction(xdr: string, network: Networks | string): Promise<string>;
}

/**
 * Passkey smart-account surface.  `accountAddress` is the `C...` contract
 * identity; `credentialId` identifies the passkey used to sign the
 * __check_auth entry.  The signer resolves this asynchronously.
 */
export interface PasskeyAccountRef {
  accountAddress: string;
  credentialId?: string;
}

export interface PasskeySigner {
  signUnsignedXdr(
    unsignedXdr: string,
    account: PasskeyAccountRef,
    latestLedger?: number,
  ): Promise<string>;
  account: PasskeyAccountRef;
}

export interface SetProfileInput {
  displayName?: string;
  bio?: string;
  avatarUrl?: string;
  xHandle?: string;
  discordHandle?: string;
  isPrivate?: boolean;
}

function isContractAddress(addr: string): boolean {
  return /^C[A-Z0-9]{55}$/.test(addr);
}

function networkPassphrase(): Networks {
  const n = (process.env.REACT_APP_STELLAR_NETWORK || 'testnet').toLowerCase();
  return n === 'mainnet' || n === 'public' ? Networks.PUBLIC : Networks.TESTNET;
}

function sorobanRpcServer(): sorobanRpc.Server {
  let rpcUrl = process.env.REACT_APP_SOROBAN_RPC_URL;
  if (!rpcUrl && typeof window !== 'undefined') {
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    rpcUrl = isDev
      ? `${window.location.protocol}//${window.location.hostname}:8080/api/soroban-rpc`
      : `${window.location.protocol}//${window.location.host}/api/soroban-rpc`;
  } else if (!rpcUrl) {
    rpcUrl = 'https://soroban-testnet.stellar.org';
  }
  return new sorobanRpc.Server(rpcUrl, { allowHttp: true });
}

async function callSetProfileViaSmartAccount(
  signer: PasskeySigner,
  input: SetProfileInput,
): Promise<ProfileWriteResult> {
  const prepUrl = `${appBackendBaseUrl()}/api/passkey-kit/prepare-profile-set`;
  const prep = await fetch(prepUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      smartAccountAddress: signer.account.accountAddress,
      profileContractId:   profileContractId(),
      displayName:   input.displayName   ?? '',
      bio:           input.bio           ?? '',
      avatarUrl:     input.avatarUrl     ?? '',
      xHandle:       input.xHandle       ?? '',
      discordHandle: input.discordHandle ?? '',
      isPrivate:     Boolean(input.isPrivate),
    }),
  });
  if (!prep.ok) {
    const err = await prep.json().catch(() => ({ error: prep.statusText }));
    throw new Error(err.error || `prepare-profile-set failed: HTTP ${prep.status}`);
  }
  const { unsignedXdr, latestLedger } = await prep.json();
  const signedXdr = await signer.signUnsignedXdr(unsignedXdr, signer.account, latestLedger);

  const submit = await fetch(`${appBackendBaseUrl()}/api/passkey-kit/fee-pay-and-submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ xdrWithPasskeySig: signedXdr }),
  });
  if (!submit.ok) {
    const err = await submit.json().catch(() => ({ error: submit.statusText }));
    throw new Error(err.error || `fee-pay-and-submit failed: HTTP ${submit.status}`);
  }
  const { hash } = await submit.json();
  return { txHash: hash };
}

async function callSetPrivacyViaSmartAccount(
  signer: PasskeySigner,
  isPrivate: boolean,
): Promise<ProfileWriteResult> {
  const prepUrl = `${appBackendBaseUrl()}/api/passkey-kit/prepare-profile-privacy`;
  const prep = await fetch(prepUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      smartAccountAddress: signer.account.accountAddress,
      profileContractId:   profileContractId(),
      isPrivate,
    }),
  });
  if (!prep.ok) {
    const err = await prep.json().catch(() => ({ error: prep.statusText }));
    throw new Error(err.error || `prepare-profile-privacy failed: HTTP ${prep.status}`);
  }
  const { unsignedXdr, latestLedger } = await prep.json();
  const signedXdr = await signer.signUnsignedXdr(unsignedXdr, signer.account, latestLedger);

  const submit = await fetch(`${appBackendBaseUrl()}/api/passkey-kit/fee-pay-and-submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ xdrWithPasskeySig: signedXdr }),
  });
  if (!submit.ok) {
    const err = await submit.json().catch(() => ({ error: submit.statusText }));
    throw new Error(err.error || `fee-pay-and-submit failed: HTTP ${submit.status}`);
  }
  const { hash } = await submit.json();
  return { txHash: hash };
}

async function invokeWithFreighter(
  wallet: SigningWallet,
  functionName: string,
  args: xdr.ScVal[],
): Promise<ProfileWriteResult> {
  const publicKey = await wallet.getPublicKey();
  const network = networkPassphrase();
  const rpcServer = sorobanRpcServer();
  const contract = new Contract(profileContractId());

  const sourceAccount = await rpcServer.getAccount(publicKey);
  const tx = new TransactionBuilder(sourceAccount, {
    fee: BASE_FEE,
    networkPassphrase: network,
  })
    .addOperation(contract.call(functionName, ...args))
    .setTimeout(60)
    .build();

  const prepared = await rpcServer.prepareTransaction(tx);
  const signedXdr = await wallet.signTransaction(prepared.toXDR(), network);
  const restored = TransactionBuilder.fromXDR(signedXdr, network);

  const send = await rpcServer.sendTransaction(restored);
  if (send.status === 'ERROR') {
    throw new Error(`Transaction submission failed: ${JSON.stringify(send.errorResult)}`);
  }
  const hash = send.hash;

  for (let i = 0; i < 30; i++) {
    await new Promise((r) => setTimeout(r, 1000));
    try {
      const info = await rpcServer.getTransaction(hash);
      if (info.status === sorobanRpc.Api.GetTransactionStatus.SUCCESS) return { txHash: hash };
      if (info.status === sorobanRpc.Api.GetTransactionStatus.FAILED) {
        throw new Error(`Transaction failed (${hash})`);
      }
    } catch (err: any) {
      if (!String(err?.message || '').includes('not found')) throw err;
    }
  }
  throw new Error(`Transaction ${hash} did not confirm within timeout`);
}

function stringScVal(s: string): xdr.ScVal {
  return xdr.ScVal.scvString(s);
}

function addrScVal(addr: string): xdr.ScVal {
  return new Address(addr).toScVal();
}

export async function setProfile(args: {
  owner: string;
  input: SetProfileInput;
  passkey?: PasskeySigner;
  wallet?: SigningWallet;
}): Promise<ProfileWriteResult> {
  const { owner, input, passkey, wallet } = args;

  const v = validateProfile(input);
  if (v) {
    throw new Error(`${v.field} is ${v.actualBytes} bytes but the on-chain cap is ${v.maxBytes}.`);
  }

  if (isContractAddress(owner)) {
    if (!passkey || passkey.account.accountAddress !== owner) {
      throw new Error('A passkey signer matching the owner smart account is required.');
    }
    return callSetProfileViaSmartAccount(passkey, input);
  }

  if (!wallet) {
    throw new Error('A Stellar wallet (Freighter / WalletConnect) is required to sign profile writes for G... accounts.');
  }
  return invokeWithFreighter(wallet, 'set_profile', [
    addrScVal(owner),
    stringScVal(input.displayName   ?? ''),
    stringScVal(input.bio           ?? ''),
    stringScVal(input.avatarUrl     ?? ''),
    stringScVal(input.xHandle       ?? ''),
    stringScVal(input.discordHandle ?? ''),
    xdr.ScVal.scvBool(Boolean(input.isPrivate)),
  ]);
}

export async function setPrivacy(args: {
  owner: string;
  isPrivate: boolean;
  passkey?: PasskeySigner;
  wallet?: SigningWallet;
}): Promise<ProfileWriteResult> {
  const { owner, isPrivate, passkey, wallet } = args;

  if (isContractAddress(owner)) {
    if (!passkey || passkey.account.accountAddress !== owner) {
      throw new Error('A passkey signer matching the owner smart account is required.');
    }
    return callSetPrivacyViaSmartAccount(passkey, isPrivate);
  }

  if (!wallet) {
    throw new Error('A Stellar wallet (Freighter / WalletConnect) is required to sign profile writes for G... accounts.');
  }
  return invokeWithFreighter(wallet, 'set_privacy', [
    addrScVal(owner),
    xdr.ScVal.scvBool(Boolean(isPrivate)),
  ]);
}
